import { useMemo } from 'react';
import { analyzeComposite, type CompositeInput, type AdvancementInfo, type AcademicSubject, LEVEL_LABELS, ACADEMIC_LEVEL_LABELS, buildLearningRadarChart } from '@/lib/compositeAnalysis';

interface Props {
  input: CompositeInput;
  printable?: boolean;
}

const levelColors: Record<number, string> = {
  1: 'text-red-600 bg-red-50 border-red-200',
  2: 'text-amber-600 bg-amber-50 border-amber-200',
  3: 'text-secondary-700 bg-secondary-50 border-secondary-200',
  4: 'text-primary-700 bg-primary-50 border-primary-200',
  5: 'text-primary-800 bg-primary-100 border-primary-300',
};

const levelBarColors: Record<number, string> = {
  1: 'bg-red-400',
  2: 'bg-amber-400',
  3: 'bg-secondary-400',
  4: 'bg-primary-400',
  5: 'bg-primary-600',
};

function AcademicAbilityBar({ subj }: { subj: AcademicSubject }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-foreground-700 w-14 flex-shrink-0 font-medium">{subj.name}</span>
      <div className="flex-1 bg-background-200 rounded-full h-2.5 overflow-hidden">
        <div
          className={`${levelBarColors[subj.level]} h-full rounded-full transition-all`}
          style={{ width: `${subj.level * 20}%` }}
        />
      </div>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${levelColors[subj.level]}`}>
        {subj.levelLabel}
      </span>
    </div>
  );
}

function AdvancementBar({ info }: { info: AdvancementInfo }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-foreground-700 w-14 flex-shrink-0 font-medium">{info.name}</span>
      <div className="flex-1 bg-background-200 rounded-full h-2.5 overflow-hidden">
        <div
          className={`${levelBarColors[info.level]} h-full rounded-full transition-all`}
          style={{ width: `${info.level * 20}%` }}
        />
      </div>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${levelColors[info.level]}`}>
        {info.levelLabel}
      </span>
    </div>
  );
}

function LearningCapabilityRadar({ subjects }: { subjects: AcademicSubject[] }) {
  const chart = buildLearningRadarChart(subjects);
  return (
    <svg viewBox={`0 0 ${chart.viewWidth} ${chart.viewHeight}`} className="w-full h-auto max-w-[320px] mx-auto block">
      {/* 격자 다각형 (레벨 1~5) */}
      {chart.grids.map((pts, i) => (
        <polygon
          key={i}
          points={pts}
          fill="none"
          stroke="currentColor"
          strokeOpacity={i === chart.grids.length - 1 ? 0.55 : 0.16}
          strokeWidth={1}
          className="text-foreground-400"
        />
      ))}
      {/* 축선 */}
      {chart.axes.map((a, i) => (
        <line
          key={i}
          x1={chart.centerX}
          y1={chart.centerY}
          x2={a.axisX}
          y2={a.axisY}
          stroke="currentColor"
          strokeOpacity={0.22}
          strokeWidth={1}
          className="text-foreground-400"
        />
      ))}
      {/* 데이터 다각형 */}
      <polygon
        points={chart.dataPolygon}
        fill="currentColor"
        fillOpacity={0.18}
        stroke="currentColor"
        strokeWidth={2}
        strokeLinejoin="round"
        className="text-primary-500"
      />
      {/* 꼭짓점 */}
      {chart.axes.map((a, i) => (
        <circle
          key={i}
          cx={a.dataX}
          cy={a.dataY}
          r={3.5}
          fill="currentColor"
          className="text-primary-600"
        />
      ))}
      {/* 라벨 */}
      {chart.axes.map((a, i) => (
        <text
          key={i}
          x={a.labelX}
          y={a.labelY}
          textAnchor={a.labelAnchor}
          dominantBaseline="central"
          fontSize={13}
          fill="currentColor"
          fontWeight={600}
          className="text-foreground-700"
        >
          {a.label}
        </text>
      ))}
    </svg>
  );
}

export default function CompositeReport({ input, printable = false }: Props) {
  const result = useMemo(() => analyzeComposite(input), [input]);

  if (!result.hasAllResults) {
    return (
      <div className={`bg-accent-50 border border-accent-200 rounded-xl p-5 ${printable ? '' : 'mb-4'}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-accent-500 rounded-xl flex-shrink-0">
            <i className="ri-sparkling-line text-white text-lg" />
          </div>
          <div>
            <h3 className="font-bold text-foreground-950 text-base mb-1">종합 융합 분석</h3>
            <p className="text-foreground-600 text-sm leading-relaxed">
              아직 아래 검사 결과가 부족해 종합 분석을 제공할 수 없습니다.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {result.missingTests.map((t) => (
                <span key={t} className="text-xs bg-white border border-accent-200 text-accent-700 px-2.5 py-1 rounded-full">
                  {t} 필요
                </span>
              ))}
            </div>
            <p className="text-foreground-500 text-xs mt-2">
              모든 검사를 완료하면 AI가 전체 결과를 종합하여 진로·과목·학습법을 예측합니다.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${printable ? '' : 'mb-6'}`}>
      {/* 헤더 */}
      <div className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl p-6 md:p-8 text-white text-center mb-5">
        <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-3">
          <i className="ri-sparkling-2-line text-3xl text-white" />
        </div>
        <h2 className="font-heading text-xl md:text-2xl font-bold mb-2">
          🔮 종합 융합 분석 결과
        </h2>
        <p className="text-white/80 text-sm">
          전체 검사 결과를 AI가 종합 분석하여 최적의 진로·과목·학습 전략을 도출했습니다
        </p>

        {/* 요약 태그 */}
        {result.summaryTags.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {result.summaryTags.map((tag) => (
              <span key={tag} className="text-xs bg-white/20 border border-white/30 text-white px-3 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 학습역량 육각형 분석 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h3 className="font-bold text-foreground-950 mb-1 flex items-center gap-2">
          <i className="ri-radar-line text-primary-500" />
          학습역량 육각형 분석
        </h3>
        <p className="text-xs text-foreground-500 mb-4">
          6대 교과(국어·영어·수학·사회·과학·예체능)의 학업 수행능력을 종합적으로 시각화했습니다.
        </p>
        <LearningCapabilityRadar subjects={result.academicAbility.subjects} />
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
          {result.academicAbility.subjects.map((s) => (
            <div key={s.name} className="flex items-center gap-1.5 text-xs text-foreground-600">
              <span className="w-2.5 h-2.5 rounded-full bg-primary-500 inline-block"></span>
              {s.name} <strong className="text-foreground-900">{s.levelLabel}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* 최종 추천 — 진로 플로우 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h3 className="font-bold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-road-map-line text-primary-500" />
          최종 진로·전공·직업 예측
        </h3>

        {/* 종합 분석 문장 */}
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-4">
          <p className="text-primary-800 text-sm leading-relaxed font-medium">
            {result.finalRecommendation.description}
          </p>
        </div>

        {/* 플로우 차트 느낌 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* 추천 계열 */}
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 text-center">
            <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg mx-auto mb-2">
              <i className="ri-compass-3-line text-white text-sm" />
            </div>
            <div className="text-xs text-foreground-500 mb-1">추천 계열</div>
            {result.finalRecommendation.careerTracks.map((track, i) => (
              <div key={track} className={`font-bold text-sm ${i === 0 ? 'text-primary-700' : 'text-foreground-600'}`}>
                {i === 0 && '⭐ '}{track}
              </div>
            ))}
          </div>

          {/* 추천 전공 */}
          <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 text-center">
            <div className="w-8 h-8 flex items-center justify-center bg-accent-500 rounded-lg mx-auto mb-2">
              <i className="ri-building-4-line text-white text-sm" />
            </div>
            <div className="text-xs text-foreground-500 mb-1">추천 전공</div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {result.finalRecommendation.majors.slice(0, 3).map((major) => (
                <span key={major} className="text-xs font-medium text-accent-800 bg-white border border-accent-200 px-2 py-0.5 rounded-full">
                  {major}
                </span>
              ))}
            </div>
          </div>

          {/* 추천 직업 */}
          <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4 text-center">
            <div className="w-8 h-8 flex items-center justify-center bg-secondary-500 rounded-lg mx-auto mb-2">
              <i className="ri-briefcase-4-line text-white text-sm" />
            </div>
            <div className="text-xs text-foreground-500 mb-1">추천 직업</div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {result.finalRecommendation.jobs.slice(0, 3).map((job) => (
                <span key={job} className="text-xs font-medium text-secondary-800 bg-white border border-secondary-200 px-2 py-0.5 rounded-full">
                  {job}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 과목별 학업수행능력 분석 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h3 className="font-bold text-foreground-950 mb-2 flex items-center gap-2">
          <i className="ri-book-read-line text-accent-500" />
          과목별 학업수행능력 분석
        </h3>
        <div className="bg-accent-50 border border-accent-200 rounded-xl p-3 mb-4">
          <p className="text-sm text-accent-800 leading-relaxed">{result.academicAbility.summary}</p>
        </div>
        <div className="space-y-4">
          {result.academicAbility.subjects.map((subj) => (
            <div key={subj.name}>
              <AcademicAbilityBar subj={subj} />
              <p className="text-xs text-foreground-500 mt-1 ml-[68px]">{subj.reason}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-background-200">
          <span className="text-xs text-foreground-400">수행 수준:</span>
          {Object.entries(ACADEMIC_LEVEL_LABELS).map(([lvl, label]) => (
            <span key={lvl} className={`text-xs px-2 py-0.5 rounded-full border ${levelColors[Number(lvl)]}`}>
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* 과목별 강점 / 약점 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {/* 강점 */}
        <div className="bg-background-50 border border-primary-200 rounded-xl p-5">
          <h3 className="font-bold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-trophy-line text-primary-500" />
            강점 과목
          </h3>
          <div className="space-y-3">
            {result.subjectAnalysis.strengths.map((s, i) => (
              <div key={s.name} className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-primary-500 text-white rounded-lg flex-shrink-0 text-xs font-bold">
                  {i + 1}
                </div>
                <div>
                  <div className="font-semibold text-foreground-900 text-sm">{s.name}</div>
                  <p className="text-xs text-foreground-500 mt-0.5">{s.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 약점 */}
        <div className="bg-background-50 border border-red-200 rounded-xl p-5">
          <h3 className="font-bold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-close-circle-line text-red-400" />
            보완이 필요한 과목
          </h3>
          <div className="space-y-3">
            {result.subjectAnalysis.weaknesses.map((w, i) => (
              <div key={w.name} className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-red-400 text-white rounded-lg flex-shrink-0 text-xs font-bold">
                  {i + 1}
                </div>
                <div>
                  <div className="font-semibold text-foreground-900 text-sm">{w.name}</div>
                  <p className="text-xs text-foreground-500 mt-0.5">{w.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 극복 방안 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h3 className="font-bold text-foreground-950 mb-3 flex items-center gap-2">
          <i className="ri-lightbulb-flash-line text-accent-500" />
          약점 과목 극복 전략
        </h3>
        <div className="space-y-2 mb-4">
          {result.weaknessRemedy.strategies.map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-foreground-700">
              <i className="ri-check-line text-accent-500 flex-shrink-0 mt-0.5" />
              {s}
            </div>
          ))}
        </div>
        <div className="bg-accent-50 border border-accent-200 rounded-xl p-3">
          <div className="text-xs font-semibold text-accent-700 mb-1">맞춤 공부법</div>
          <p className="text-sm text-accent-800 leading-relaxed">{result.weaknessRemedy.studyMethod}</p>
        </div>
      </div>

      {/* 선행학습 권장 수준 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h3 className="font-bold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-bar-chart-grouped-line text-primary-500" />
          과목별 선행학습 권장 수준
        </h3>
        <div className="space-y-3">
          {result.subjectAdvancement.map((adv) => (
            <div key={adv.name}>
              <AdvancementBar info={adv} />
              <p className="text-xs text-foreground-500 mt-1 ml-[68px]">{adv.reason}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-background-200">
          <span className="text-xs text-foreground-400">수준 기준:</span>
          {Object.entries(levelColors).map(([lvl, cls]) => (
            <span key={lvl} className={`text-xs px-2 py-0.5 rounded-full border ${cls}`}>
              {LEVEL_LABELS[Number(lvl)]}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}